import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
} from '@react-pdf/renderer'
import type { CV, DateFormat, CVLanguage } from '../../types/cv'
import { techPalettes, type TechColors } from '../../utils/colorPalettes'
import { getCVLabels, getLevelLabel } from '../../utils/cvTranslations'
import { EmailIcon, PhoneIcon, LocationIcon, BirthDateIcon } from './ContactIcons'


function getColors(paletteId?: string): TechColors {
  return techPalettes[paletteId || 'dark'] || techPalettes.dark
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 40,
    paddingHorizontal: 0,
    fontFamily: 'Courier',
    fontSize: 9,
    lineHeight: 1.6,
  },
  gutterFixed: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 25,
  },
  content: {
    marginLeft: 30,
    marginRight: 25,
  },
  header: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomStyle: 'dashed',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  headerPhoto: {
    width: 65,
    height: 65,
    borderRadius: 4,
    objectFit: 'cover',
  },
  headerInfo: {
    flex: 1,
  },
  prompt: {
    fontSize: 9,
    marginBottom: 2,
  },
  name: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  title: {
    fontSize: 10,
    marginBottom: 8,
  },
  contactRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    fontSize: 8,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 8,
    textDecoration: 'none',
  },
  contactIcon: {
    fontSize: 7,
    marginRight: 3,
  },
  linksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 6,
  },
  link: {
    fontSize: 8,
  },
  section: {
    marginBottom: 14,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  sectionLine: {
    flex: 1,
    height: 1,
    marginLeft: 8,
  },
  paragraph: {
    fontSize: 9,
  },
  entryContainer: {
    marginBottom: 10,
  },
  entryTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  entryTitle: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  entryDate: {
    fontSize: 8,
  },
  entrySubtitle: {
    fontSize: 9,
  },
  entryLocation: {
    fontSize: 8,
    marginTop: 1,
  },
  entryDescription: {
    fontSize: 8,
    marginTop: 4,
    paddingLeft: 8,
    borderLeftWidth: 2,
  },
  skillsSection: {
    marginBottom: 14,
  },
  skillsCategory: {
    marginBottom: 6,
  },
  skillsCategoryTitle: {
    fontSize: 8,
    marginBottom: 3,
  },
  skillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 5,
  },
  skillTag: {
    fontSize: 8,
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRadius: 2,
    borderWidth: 1,
  },
  languagesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  languageItem: {
    fontSize: 9,
  },
})

const localeMap: Record<CVLanguage, string> = {
  es: 'es-ES',
  en: 'en-US',
  fr: 'fr-FR',
  pt: 'pt-PT',
  de: 'de-DE',
}

function formatDate(
  dateStr: string | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'present'
): string {
  if (!dateStr) return ''
  const str = dateStr.trim().toLowerCase()
  const locale = localeMap[language] || 'en-US'
  if (str.includes('present') || str.includes('actual') || str.includes('current')) {
    return presentLabel.toLowerCase()
  }
  const yearOnly = format === 'year-only'
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-')
    if (yearOnly) return year
    const date = new Date(parseInt(year), parseInt(month) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' }).toLowerCase()
    }
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    if (yearOnly) return dateStr.split('-')[0]
    const date = new Date(dateStr)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' }).toLowerCase()
    }
  }
  if (/^\d{4}$/.test(dateStr)) {
    return dateStr
  }
  const mmYYYYMatch = dateStr.match(/^(\d{1,2})[/-](\d{4})$/)
  if (mmYYYYMatch) {
    if (yearOnly) return mmYYYYMatch[2]
    const date = new Date(parseInt(mmYYYYMatch[2]), parseInt(mmYYYYMatch[1]) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' }).toLowerCase()
    }
  }
  const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/)
  if (yearMatch) {
    return yearMatch[0]
  }
  return ''
}

function formatDateRange(
  start: string | undefined,
  end: string | undefined,
  current: boolean | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'present'
): string {
  const startStr = formatDate(start, format, language, presentLabel)
  const endStr = current ? presentLabel.toLowerCase() : formatDate(end, format, language, presentLabel)
  if (!startStr && !endStr) return ''
  if (!startStr) return endStr
  if (!endStr) return startStr
  return `${startStr} -> ${endStr}`
}


interface TechTemplateProps {
  cv: CV
  colorPalette?: string
}


export function TechTemplate({ cv, colorPalette }: TechTemplateProps) {
  const colors = getColors(colorPalette)
  const labels = getCVLabels(cv.settings?.language, cv.settings?.customLabels)
  const showIcons = cv.settings?.showContactIcons ?? true
  const hasPersonalInfo =
    cv.personalInfo?.fullName ||
    cv.personalInfo?.title ||
    cv.personalInfo?.email ||
    cv.personalInfo?.phone ||
    cv.personalInfo?.location

  const hasExperience = cv.experience && cv.experience.length > 0
  const hasEducation = cv.education && cv.education.length > 0
  const hasSkills =
    (cv.skills?.technical && cv.skills.technical.length > 0) ||
    (cv.skills?.soft && cv.skills.soft.length > 0) ||
    (cv.skills?.tools && cv.skills.tools.length > 0)
  const hasLanguages = cv.languages && cv.languages.some((lang) => lang.name)

  const userName = cv.personalInfo?.fullName?.toLowerCase().replace(/\s+/g, '_') || 'profile'

  return (
    <Document
      title={`CV - ${cv.personalInfo?.fullName || 'Curriculum Vitae'}`}
      author={cv.personalInfo?.fullName || ''}
      subject={cv.personalInfo?.title || 'Curriculum Vitae'}
      creator="CVtoday"
      keywords={`CV, resume, ${cv.personalInfo?.fullName || ''}, ${cv.personalInfo?.title || ''}`}
    >
      <Page size="A4" style={[styles.page, { backgroundColor: colors.background, color: colors.text }]}>
        <View style={[styles.gutterFixed, { backgroundColor: colors.gutter }]} fixed />

        <View style={styles.content} wrap={true}>
          {hasPersonalInfo && (
            <View style={[styles.header, { borderBottomColor: colors.border }]}>
              <View style={styles.headerContent}>
                {cv.personalInfo?.photo && (
                  <Image
                    src={cv.personalInfo.photo}
                    style={[styles.headerPhoto, { borderWidth: 2, borderColor: colors.accent }]}
                  />
                )}
                <View style={styles.headerInfo}>
                  <Text style={[styles.prompt, { color: colors.accent }]}>
                    {`> ~/developer/${userName}`}
                  </Text>

                  {cv.personalInfo?.fullName && (
                    <Text style={[styles.name, { color: colors.text }]}>
                      {cv.personalInfo.fullName}
                    </Text>
                  )}
                  {cv.personalInfo?.title && (
                    <Text style={[styles.title, { color: colors.keyword }]}>
                      {cv.personalInfo.title}
                    </Text>
                  )}

                  <View style={styles.contactRow}>
                    {cv.personalInfo?.email && (
                      <Link src={`mailto:${cv.personalInfo.email}`} style={styles.contactItem}>
                        {showIcons ? (
                          <EmailIcon size={9} color={colors.textMuted} style={{ marginRight: 4, marginBottom: 1 }} />
                        ) : (
                          <Text style={{ color: colors.textMuted }}>email: </Text>
                        )}
                        <Text style={{ color: colors.textMuted }}>{cv.personalInfo.email}</Text>
                      </Link>
                    )}
                    {cv.personalInfo?.phone && (
                      <Link src={`tel:${cv.personalInfo.phone}`} style={styles.contactItem}>
                        {showIcons ? (
                          <PhoneIcon size={9} color={colors.textMuted} style={{ marginRight: 4, marginBottom: 1 }} />
                        ) : (
                          <Text style={{ color: colors.textMuted }}>tel: </Text>
                        )}
                        <Text style={{ color: colors.textMuted }}>{cv.personalInfo.phone}</Text>
                      </Link>
                    )}
                    {cv.personalInfo?.location && (
                      <View style={styles.contactItem}>
                        {showIcons ? (
                          <LocationIcon size={9} color={colors.textMuted} style={{ marginRight: 4, marginBottom: 1 }} />
                        ) : (
                          <Text style={{ color: colors.textMuted }}>loc: </Text>
                        )}
                        <Text style={{ color: colors.textMuted }}>{cv.personalInfo.location}</Text>
                      </View>
                    )}
                    {cv.personalInfo?.birthDate && (
                      <View style={styles.contactItem}>
                        {showIcons ? (
                          <BirthDateIcon size={9} color={colors.textMuted} style={{ marginRight: 4, marginBottom: 1 }} />
                        ) : (
                          <Text style={{ color: colors.textMuted }}>dob: </Text>
                        )}
                        <Text style={{ color: colors.textMuted }}>{cv.personalInfo.birthDate}</Text>
                      </View>
                    )}
                  </View>

                  {cv.personalInfo?.links && cv.personalInfo.links.length > 0 && (
                    <View style={styles.linksRow}>
                      {cv.personalInfo.links.map((link) =>
                        link.url ? (
                          <Link key={link.id} src={link.url} style={[styles.link, { color: colors.accent }]}>
                            {`<${link.label || 'link'}/>`}
                          </Link>
                        ) : null
                      )}
                    </View>
                  )}
                </View>
              </View>
            </View>
          )}

          {cv.summary && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionTitle, { color: colors.comment }]}>
                  {`// ${labels.summary.toUpperCase()}`}
                </Text>
                <View style={[styles.sectionLine, { backgroundColor: colors.border }]} />
              </View>
              <Text style={[styles.paragraph, { color: colors.textMuted }]}>
                {cv.summary}
              </Text>
            </View>
          )}

          {hasExperience && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionTitle, { color: colors.comment }]}>
                  {`// ${labels.experience.toUpperCase()}`}
                </Text>
                <View style={[styles.sectionLine, { backgroundColor: colors.border }]} />
              </View>
              {cv.experience?.map((exp) => (
                <View key={exp.id} style={styles.entryContainer}>
                  <View style={styles.entryTitleRow}>
                    <Text style={[styles.entryTitle, { color: colors.function }]}>
                      {exp.position || 'Position'}
                    </Text>
                    <Text style={[styles.entryDate, { color: colors.number }]}>
                      {formatDateRange(exp.startDate, exp.endDate, exp.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                    </Text>
                  </View>
                  {exp.company && (
                    <Text style={[styles.entrySubtitle, { color: colors.keyword }]}>
                      {`@${exp.company}`}
                    </Text>
                  )}
                  {exp.location && (
                    <Text style={[styles.entryLocation, { color: colors.textMuted }]}>
                      {exp.location}
                    </Text>
                  )}
                  {exp.description && (
                    <Text style={[styles.entryDescription, { color: colors.textMuted, borderLeftColor: colors.accent }]}>
                      {exp.description}
                    </Text>
                  )}
                </View>
              ))}
              <View style={{ marginBottom: 14 }} />
            </>
          )}

          {hasEducation && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionTitle, { color: colors.comment }]}>
                  {`// ${labels.education.toUpperCase()}`}
                </Text>
                <View style={[styles.sectionLine, { backgroundColor: colors.border }]} />
              </View>
              {cv.education?.map((edu) => (
                <View key={edu.id} style={styles.entryContainer}>
                  <View style={styles.entryTitleRow}>
                    <Text style={[styles.entryTitle, { color: colors.function }]}>
                      {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                    </Text>
                    <Text style={[styles.entryDate, { color: colors.number }]}>
                      {formatDateRange(edu.startDate, edu.endDate, edu.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                    </Text>
                  </View>
                  {edu.institution && (
                    <Text style={[styles.entrySubtitle, { color: colors.keyword }]}>
                      {`@${edu.institution}`}
                    </Text>
                  )}
                  {edu.location && (
                    <Text style={[styles.entryLocation, { color: colors.textMuted }]}>
                      {edu.location}
                    </Text>
                  )}
                  {edu.gpa && (
                    <Text style={[styles.entryDescription, { color: colors.textMuted, borderLeftColor: colors.accent }]}>
                      {`${labels.gpa}: ${edu.gpa}`}
                    </Text>
                  )}
                </View>
              ))}
              <View style={{ marginBottom: 14 }} />
            </>
          )}

          {hasSkills && (
            <View style={styles.skillsSection} wrap={false}>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionTitle, { color: colors.comment }]}>
                  {`// ${labels.skills.toUpperCase()}`}
                </Text>
                <View style={[styles.sectionLine, { backgroundColor: colors.border }]} />
              </View>

              {cv.skills?.technical && cv.skills.technical.length > 0 && (
                <View style={styles.skillsCategory}>
                  <Text style={[styles.skillsCategoryTitle, { color: colors.keyword }]}>
                    {'const technical = ['}
                  </Text>
                  <View style={styles.skillsRow}>
                    {cv.skills.technical.map((skill, index) => (
                      <Text
                        key={index}
                        style={[
                          styles.skillTag,
                          { backgroundColor: colors.tagBg, borderColor: colors.accent, color: colors.accent },
                        ]}
                      >
                        {skill}
                      </Text>
                    ))}
                  </View>
                  <Text style={[styles.skillsCategoryTitle, { color: colors.keyword }]}>{']'}</Text>
                </View>
              )}

              {cv.skills?.tools && cv.skills.tools.length > 0 && (
                <View style={styles.skillsCategory}>
                  <Text style={[styles.skillsCategoryTitle, { color: colors.keyword }]}>
                    {'const tools = ['}
                  </Text>
                  <View style={styles.skillsRow}>
                    {cv.skills.tools.map((skill, index) => (
                      <Text
                        key={index}
                        style={[
                          styles.skillTag,
                          { backgroundColor: colors.tagBg, borderColor: colors.string, color: colors.string },
                        ]}
                      >
                        {skill}
                      </Text>
                    ))}
                  </View>
                  <Text style={[styles.skillsCategoryTitle, { color: colors.keyword }]}>{']'}</Text>
                </View>
              )}

              {cv.skills?.soft && cv.skills.soft.length > 0 && (
                <View style={styles.skillsCategory}>
                  <Text style={[styles.skillsCategoryTitle, { color: colors.keyword }]}>
                    {'const soft = ['}
                  </Text>
                  <View style={styles.skillsRow}>
                    {cv.skills.soft.map((skill, index) => (
                      <Text
                        key={index}
                        style={[
                          styles.skillTag,
                          { backgroundColor: colors.tagBg, borderColor: colors.number, color: colors.number },
                        ]}
                      >
                        {skill}
                      </Text>
                    ))}
                  </View>
                  <Text style={[styles.skillsCategoryTitle, { color: colors.keyword }]}>{']'}</Text>
                </View>
              )}
            </View>
          )}

          {hasLanguages && (
            <View style={styles.section} wrap={false}>
              <View style={styles.sectionHeader}>
                <Text style={[styles.sectionTitle, { color: colors.comment }]}>
                  {`// ${labels.languages.toUpperCase()}`}
                </Text>
                <View style={[styles.sectionLine, { backgroundColor: colors.border }]} />
              </View>
              <View style={styles.languagesRow}>
                {cv.languages?.map((lang) =>
                  lang.name ? (
                    <Text key={lang.id} style={[styles.languageItem, { color: colors.function }]}>
                      {lang.name}{lang.level ? ` (${getLevelLabel(lang.level, cv.settings?.language)})` : ''}
                    </Text>
                  ) : null
                )}
              </View>
            </View>
          )}
        </View>
      </Page>
    </Document>
  )
}
